import { Box, IconButton, Typography } from '@mui/material'
import {
  CustomLoadingOverlay,
  dateComparator,
  getStringId,
  isDateLessThan,
  relativeTime,
  sortColDefs,
} from 'components/common/AgGridUtils'
import { addItemToLS, getItemFromLS } from 'utils/localStorage'
import { cloneDeep, debounce } from 'lodash'
import { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'

import { AgGridReact } from 'ag-grid-react'
import CloseIcon from '@mui/icons-material/Close'
import ConfirmDialog from 'components/common/ConfirmDialog'
import ConfirmModal from 'components/common/ConfirmModal'
import DataBridgeRibbon from './DataBridgeRibbon'
import DataBridgeUserModal from './DataBridgeUserModal'
import Dialog from '@mui/material/Dialog'
import { Icon as Iconify } from '@iconify/react'
import ResetFiltersButton from 'components/common/ResetFiltersButton'
import SearchBarWithRecent from 'components/common/SearchBarWithRecent'
import StatusSnackbar from 'components/common/StatusSnackbar'
import { appColors } from 'utils'
import { unescapeHtml } from 'utils/htmlSymbolHandling'
import useDataBridge from 'components/common/hooks/useDataBridge'
import useInnovaTheme from 'components/common/hooks/useInnovaTheme'

const GRID_TYPE_SETTINGS = 'SETTINGS'
const GRID_TYPE_HISTORY = 'HISTORY'

const DataBridgeModal = ({ organization, onClose }) => {
  const _isMounted = useRef(true)
  const gridApiSettings = useRef(null)
  const gridApiHistory = useRef(null)
  const [selectedRow, setSelectedRow] = useState(null)
  const inputRow = useRef({})
  const { getAgGridTheme, getChartBackColor, getWindowBarColor, searchBarStyle, getBackColor } = useInnovaTheme()
  const settingsGridRef = useRef(null)
  const historyGridRef = useRef(null)
  const [searchText, setSearchText] = useState('')
  const [gridType, setGridType] = useState(GRID_TYPE_SETTINGS)
  const [status, setStatus] = useState({ show: false, severity: 'info', message: '' })
  const [confirm, setConfirm] = useState({ show: false, title: '', text: '', data: null })
  const { isLoading, isUpdating, isLoadingDropDowns, fetchDataBridgeSettings, updateDataBridgeSettings, dropDowns } =
    useDataBridge()
  const { fetchDataBridgeHistory, isLoadingHistory } = useDataBridge()
  const [filterApplied, setFilterApplied] = useState(false)
  const [dataBridgeUserModal, setDataBridgeUserModal] = useState({
    show: false,
    action: '',
    user: null,
  })

  const [changesMade, setChangesMade] = useState(false)
  const [showCloseWarning, setShowCloseWarning] = useState(false)

  const showResult = useCallback((res) => {
    setStatus({
      show: true,
      severity: res.success ? 'success' : 'error',
      message: res.message,
    })
  }, [])

  useEffect(() => {
    _isMounted.current = true
    return () => {
      _isMounted.current = false
    }
  }, [])

  useEffect(() => {
    gridType === GRID_TYPE_SETTINGS
      ? debounceSetQuickFilterSettings(searchText)
      : debounceSetQuickFilterHistory(searchText)
  }, [searchText]) // eslint-disable-line react-hooks/exhaustive-deps

  const leftAlignCell = useMemo(
    () => ({
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'start',
    }),
    [],
  )

  const debounceSetQuickFilterSettings = debounce((text) => {
    if (gridApiSettings.current) {
      gridApiSettings.current.setGridOption('quickFilterText', text)
    }
  }, 300)

  const onSelectionChangedSettings = useCallback(() => {
    if (!gridApiSettings.current) return

    const selectedRows = gridApiSettings.current.getSelectedRows()
    setSelectedRow(selectedRows.length > 0 ? cloneDeep(selectedRows[0]) : null)
  }, [])

  const saveColumnStateSettings = useCallback(() => {
    if (gridApiSettings.current) {
      const colLayout = gridApiSettings.current.getColumnState()
      if (colLayout) addItemToLS('emailSettings', 'colLayout', colLayout)
    }
  }, [])

  const onFirstDataRenderedSettings = (params) => {
    if (gridApiSettings.current) gridApiSettings.current.onFilterChanged()
    autoSizeColumnsSettings()
  }

  const autoSizeColumnsSettings = () => {
    if (gridApiSettings.current === null) {
      return
    }

    gridApiSettings.current?.autoSizeAllColumns()
  }

  const defaultColDefSettings = useMemo(() => {
    return {
      resizable: true,
      sortable: true,
      autoHeight: true,
      editable: false,
      headerClass: 'header-no-padding',
      filter: 'agSetColumnFilter',
      filterParams: {
        excelMode: 'windows',
      },
    }
  }, [])

  const handleFetchSettings = async () => {
    if (isLoading) return

    const res = await fetchDataBridgeSettings(organization)

    if (!res?.success) {
      showResult(res)
      return
    }

    if (!Array.isArray(res?.data?.settings)) return

    let rowData = []

    res.data.settings.forEach((item) => {
      if (item.hasOwnProperty('emailFrom') && item.hasOwnProperty('actions')) {
        rowData.push({
          emailFrom: item.emailFrom,
          actions: item.actions,
          surveyUnits: item.surveyUnits || '',
          emailReply: item.emailReply || '',
          mudUnits: item.mudUnits || '',
          dateFormat: item.dateFormat || '',
        })
      }
    })

    gridApiSettings.current.setGridOption('rowData', rowData)
    setSelectedRow(null)
    setChangesMade(false)
  }

  const onGridReadySettings = (params) => {
    gridApiSettings.current = params.api
    handleFetchSettings()
  }

  const getContextMenuItemsSettings = (params) => {
    return ['copy']
  }

  const getRowIdSettings = useMemo(() => {
    return (params) => {
      return getStringId(params.data?.emailFrom)
    }
  }, [])

  const onAdd = useCallback(() => {
    setDataBridgeUserModal({
      show: true,
      action: 'ADD',
      user: null,
    })
  }, [])

  const onEdit = useCallback(() => {
    if (!selectedRow) return

    setDataBridgeUserModal({
      show: true,
      action: 'EDIT',
      user: cloneDeep(selectedRow),
    })
  }, [selectedRow])

  const onDelete = useCallback(() => {
    if (!selectedRow) return

    setConfirm({
      show: true,
      title: 'Delete Data Bridge User',
      text: `Are you sure you want to delete ${selectedRow.emailFrom}?`,
      data: selectedRow,
    })
  }, [selectedRow])

  const handleClose = useCallback(() => {
    if (changesMade) {
      setShowCloseWarning(true)
      return
    }
    onClose()
  }, [changesMade, onClose])

  const handleDelete = useCallback(() => {
    if (!confirm.data?.emailFrom) return
    if (!gridApiSettings.current) return

    gridApiSettings.current.applyTransaction({
      remove: [confirm.data],
    })

    gridApiSettings.current.deselectAll()
    setSelectedRow(null)
    setChangesMade(true)

    setConfirm({
      show: false,
      title: '',
      text: '',
      data: null,
    })
  }, [confirm.data])

  const handleAddRow = useCallback(async () => {
    if (!inputRow.current.hasOwnProperty('emailFrom')) return

    if (!isValidEmail(inputRow.current.emailFrom)) return

    let duplicateFound = false
    gridApiSettings.current.forEachNode((rowNode, index) => {
      if (rowNode.data.emailFrom === inputRow.current.emailFrom) {
        duplicateFound = true
        return
      }
    })

    if (duplicateFound) {
      showResult?.({ success: false, message: `Duplicate email found: ${inputRow.current.emailFrom}` })
      return
    }

    gridApiSettings.current.applyTransaction({
      add: [inputRow.current],
    })

    let rowData = []
    gridApiSettings.current.forEachNode((rowNode, index) => {
      if (rowNode.rowPinned !== 'bottom') {
        rowData.push(rowNode.data)
      }
    })

    inputRow.current = {}
    if (gridApiSettings.current) {
      gridApiSettings.current.setGridOption('pinnedBottomRowData', [inputRow.current])
    }
  }, []) //eslint-disable-line react-hooks/exhaustive-deps

  const handleUpdate = useCallback(async () => {
    if (!gridApiSettings.current) return

    let newSettings = []
    if (gridApiSettings.current) {
      gridApiSettings.current.forEachNode((rowNode) => {
        if (rowNode.rowPinned !== 'bottom') {
          newSettings.push(rowNode.data)
        }
      })
    }

    const res = await updateDataBridgeSettings(organization, newSettings)

    if (!res?.success) {
      showResult(res)
      return
    }

    setChangesMade(false)
    onClose()
  }, [organization, updateDataBridgeSettings, onClose, showResult])

  const onCloseDataBridgeUserModal = useCallback(() => {
    setDataBridgeUserModal({
      show: false,
      action: '',
      user: null,
    })
  }, [])

  const onSaveDataBridgeUser = useCallback(
    (user) => {
      if (!gridApiSettings.current) return
      if (!user?.emailFrom) return

      if (dataBridgeUserModal.action === 'ADD') {
        let duplicateFound = false

        gridApiSettings.current.forEachNode((rowNode) => {
          if (rowNode.data?.emailFrom === user.emailFrom) {
            duplicateFound = true
          }
        })

        if (duplicateFound) {
          showResult({
            success: false,
            message: `Duplicate email found: ${user.emailFrom}`,
          })
          return
        }

        gridApiSettings.current.applyTransaction({
          add: [user],
        })
      }

      if (dataBridgeUserModal.action === 'EDIT') {
        const originalUser = dataBridgeUserModal.user
        const originalEmail = originalUser?.emailFrom

        let duplicateFound = false

        gridApiSettings.current.forEachNode((rowNode) => {
          if (rowNode.data?.emailFrom === user.emailFrom && rowNode.data?.emailFrom !== originalEmail) {
            duplicateFound = true
          }
        })

        if (duplicateFound) {
          showResult({
            success: false,
            message: `Duplicate email found: ${user.emailFrom}`,
          })
          return
        }

        gridApiSettings.current.applyTransaction({
          remove: [originalUser],
          add: [user],
        })

        gridApiSettings.current.deselectAll()

        const editedRowNode = gridApiSettings.current.getRowNode(getStringId(user.emailFrom))

        if (editedRowNode) {
          editedRowNode.setSelected(true)
        }

        setSelectedRow(cloneDeep(user))
      }

      setChangesMade(true)
      onCloseDataBridgeUserModal()
    },
    [dataBridgeUserModal, onCloseDataBridgeUserModal, showResult],
  )

  useImperativeHandle(settingsGridRef, () => ({
    handleUpdate,
    debounceSetQuickFilterHistory,
  }))

  function isValidEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
  }

  const gridOptionsSettings = useMemo(() => {
    return {
      rowSelection: {
        mode: 'singleRow',
        enableClickSelection: true,
      },
      pinnedBottomRowData: [inputRow.current],
      onCellEditingStopped: (event) => {
        if (event.node?.rowPinned === 'bottom') {
          handleAddRow()
          return
        }

        if (event.colDef.field === 'emailFrom') {
          if (!isValidEmail(event.data.emailFrom)) {
            event.node.setDataValue('emailFrom', event.oldValue)
            return
          }
        }
      },
      getRowStyle: ({ node }) => (node?.rowPinned ? { fontWeight: 'bold', fontStyle: 'italic' } : undefined),
      onDragStopped: () => {
        saveColumnStateSettings()
      },
      onColumnVisible: () => {
        saveColumnStateSettings()
      },
      loadingOverlayComponent: CustomLoadingOverlay,
    }
  }, [handleAddRow, saveColumnStateSettings])

  let columnDefsSettings = useMemo(
    () => [
      {
        headerName: 'Email',
        colId: 'emailFrom',
        field: 'emailFrom',
        cellStyle: { alignItem: 'end' },
        valueFormatter: (params) => unescapeHtml(params.value),
      },
      {
        headerName: 'Reply To',
        colId: 'emailReply',
        field: 'emailReply',
        cellStyle: { alignItem: 'end' },
        valueFormatter: (params) => unescapeHtml(params.value),
      },
      {
        headerName: 'Actions',
        colId: 'actions',
        field: 'actions',
        minWidth: 200,
        maxWidth: 600,
        autoHeight: true,
        cellStyle: {
          whiteSpace: 'normal',
          lineHeight: '1.4em',
          padding: '6px',
        },
        cellRenderer: (params) => {
          if (!params.value) return ''
          if (!Array.isArray(params.value)) return ''

          return (
            <div style={{ whiteSpace: 'normal', lineHeight: '1.4em' }}>
              {params.value?.map((row, index) => (
                <div key={index}>{row}</div>
              ))}
            </div>
          )
        },
      },
      {
        headerName: 'Depth Units',
        colId: 'surveyUnits',
        field: 'surveyUnits',
        width: 140,
        minWidth: 140,
        cellStyle: leftAlignCell,
      },
      {
        headerName: 'Mud Units',
        colId: 'mudUnits',
        field: 'mudUnits',
        width: 140,
        minWidth: 140,
        cellStyle: leftAlignCell,
      },
      {
        headerName: 'Date Format',
        colId: 'dateFormat',
        field: 'dateFormat',
        width: 200,
        minWidth: 200,
        cellStyle: leftAlignCell,
      },
    ],
    [leftAlignCell],
  )

  const dateTimeFormatter = useCallback((value) => {
    if (!value || typeof value !== 'string' || value.trim() === '') return ''
    if (isDateLessThan(value, '1990-01-01')) return ''

    const date = new Date(value)
    if (isNaN(date)) return ''

    return date.toLocaleString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    })
  }, [])

  const relativeTimeFormatter = useCallback(relativeTime, [])

  const dateFilterComparator = useCallback(dateComparator, [])

  const debounceSetQuickFilterHistory = debounce((text) => {
    if (gridApiHistory.current) {
      gridApiHistory.current.setGridOption('quickFilterText', text)
    }
  }, 300)

  const saveColumnState = useCallback(() => {
    if (gridApiHistory.current) {
      const colLayout = gridApiHistory.current.getColumnState()
      if (colLayout) addItemToLS('dataBridgeHistory', 'colLayout', colLayout)
    }
  }, [])

  const loadFilterState = () => {
    if (gridApiHistory.current) {
      const filters = getItemFromLS('dataBridgeHistory', 'filters')
      if (filters) gridApiHistory.current.setFilterModel(filters)
    }
  }

  const saveFilterState = () => {
    if (gridApiHistory.current) {
      const filters = gridApiHistory.current.getFilterModel()
      if (filters) addItemToLS('dataBridgeHistory', 'filters', filters)
      setFilterApplied(Object.keys(filters).length > 0)
    }
  }

  const onFilterChangedHistory = useCallback(() => {
    if (!gridApiHistory.current) return
    if (gridApiHistory.current.isDestroyed()) return
    setFilterApplied(Object.keys(gridApiHistory.current.getFilterModel()).length > 0)

    saveFilterState()
  }, [])

  const onFirstDataRenderedHistory = (params) => {
    loadFilterState()
    if (gridApiHistory.current) gridApiHistory.current.onFilterChanged()
    autoSizeColumns()
  }

  const autoSizeColumns = () => {
    if (gridApiHistory.current === null) {
      return
    }

    gridApiHistory.current?.autoSizeAllColumns()
  }

  const defaultColDefHistory = useMemo(() => {
    return {
      resizable: true,
      sortable: true,
      autoHeight: true,
      editable: false,
      headerClass: 'header-no-padding',
      filter: 'agSetColumnFilter',
      filterParams: {
        excelMode: 'windows',
      },
    }
  }, [])

  const handleFetchHistory = async () => {
    const res = await fetchDataBridgeHistory()

    if (!res?.success) {
      showResult(res)
      return
    }

    if (!Array.isArray(res.data)) return

    let rowData = []
    res.data.forEach((item) => {
      if (item.hasOwnProperty('uid')) {
        rowData.push(item)
      }
    })

    gridApiHistory.current.setGridOption('rowData', rowData)
  }

  const onGridReadyHistory = (params) => {
    gridApiHistory.current = params.api
    handleFetchHistory()
  }

  const getContextMenuItemsHistory = (params) => {
    return ['copy']
  }

  const getRowIdHistory = useMemo(() => {
    return (params) => {
      return getStringId(params.data?.uid)
    }
  }, [])

  useImperativeHandle(historyGridRef, () => ({
    debounceSetQuickFilterHistory,
  }))

  const gridOptionsHistory = useMemo(() => {
    return {
      sideBar: {
        toolPanels: [
          {
            id: 'filters',
            labelDefault: 'Filters',
            labelKey: 'filters',
            iconKey: 'filter',
            toolPanel: 'agFiltersToolPanel',
          },
          {
            id: 'columns',
            labelDefault: 'Columns',
            labelKey: 'columns',
            iconKey: 'columns',
            toolPanel: 'agColumnsToolPanel',
          },
        ],
        defaultToolPanel: '',
        position: 'left',
      },
      onDragStopped: (event) => {
        saveColumnState()
      },
      onColumnVisible: (event) => {
        saveColumnState()
      },
      loadingOverlayComponent: CustomLoadingOverlay,
    }
  }, [saveColumnState])

  let columnDefsHistory = useMemo(
    () => [
      {
        headerName: '',
        colId: 'status',
        field: 'fileName',
        width: 60,
        pinned: 'left',
        lockPosition: 'left',
        sortable: false,
        filter: false,
        suppressHeaderMenuButton: true,
        suppressHeaderFilterButton: true,
        cellStyle: {
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 0,
        },
        headerComponent: ResetFiltersButton,
        headerComponentParams: {
          filterApplied: filterApplied,
          gridApi: gridApiHistory,
        },
        cellRenderer: (params) => {
          const fileName = params.value || ''
          const isFail = fileName.startsWith('FAIL_')
          let errorText = ''

          try {
            if (params.data?.jsonData) {
              const parsed = JSON.parse(params.data.jsonData)
              if (isFail) {
                if (parsed?.errorText && parsed?.errorText.trim() !== '') {
                  errorText = parsed.errorText.trim()
                } else {
                  errorText = 'Unknown error'
                }
              }
            }
          } catch (err) {
            if (isFail) errorText = 'Invalid JSON data'
          }

          const icon = isFail ? 'mdi:alert-circle-outline' : 'mdi:check-circle-outline'
          const color = isFail ? '#f39c12' : '#2ecc71'
          const tooltip = isFail ? errorText : 'Success'

          return (
            <span
              title={tooltip}
              style={{
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
              <Iconify icon={icon} color={color} fontSize={20} />
            </span>
          )
        },
      },
      {
        headerName: 'Time',
        colId: 'timeStamp',
        field: 'timeStamp',
        cellStyle: { alignItem: 'start' },
        pinned: 'left',
        lockPosition: 'left',
        filter: 'agDateColumnFilter',
        filterParams: {
          comparator: dateFilterComparator,
        },
        valueFormatter: (params) => {
          return `${dateTimeFormatter(params.value)} [${relativeTimeFormatter(params.value)}]`
        },
      },
      {
        headerName: 'From',
        colId: 'emailFrom',
        field: 'jsonData',
        width: 300,
        valueGetter: (params) => {
          try {
            const parsed = JSON.parse(params.data?.jsonData || '{}')
            let from = parsed.from || ''
            if (!from) return ''

            const match = from.match(/<([^>]+)>/)
            if (match) {
              from = match[1]
            }

            from = from.replace(/^"|"$/g, '').trim()

            return from
          } catch {
            return ''
          }
        },
      },

      {
        headerName: 'Organization',
        colId: 'organization',
        field: 'jsonData',
        width: 180,
        valueGetter: (params) => {
          try {
            const parsed = JSON.parse(params.data?.jsonData || '{}')

            let to = ''

            if (Array.isArray(parsed.to)) {
              to = parsed.to.find((t) => t?.endsWith('@feed.innova-drilling.com')) || ''
            } else if (typeof parsed.to === 'string' && parsed.to.endsWith('@feed.innova-drilling.com')) {
              to = parsed.to
            }

            if (!to) return ''

            return to.replace('@feed.innova-drilling.com', '').toUpperCase()
          } catch {
            return ''
          }
        },
      },
      {
        headerName: 'Action',
        colId: 'action',
        field: 'action',
        width: 180,
      },
      {
        headerName: 'Email Info',
        colId: 'jsonData',
        field: 'jsonData',
        flex: 1,
        autoHeight: true,
        cellDataType: false,
        sortable: false,
        filter: false,
        cellStyle: {
          whiteSpace: 'normal',
          lineHeight: '1.4em',
          padding: '6px',
        },
        cellRenderer: (params) => {
          if (!params.value) return ''

          let data
          try {
            data = JSON.parse(params.value)
          } catch {
            return '(Invalid JSON)'
          }

          const from = data.from || ''
          const to = Array.isArray(data.to) ? data.to.join(', ') : data.to || ''
          const subject = data.subject || ''

          return (
            <div style={{ whiteSpace: 'normal', lineHeight: '1.4em' }}>
              <div>
                <strong>From:</strong> {from}
              </div>
              <div>
                <strong>To:</strong> {to}
              </div>
              <div>
                <strong>Subject:</strong> {subject}
              </div>
            </div>
          )
        },
      },
    ],
    [filterApplied, dateTimeFormatter, relativeTimeFormatter, dateFilterComparator],
  )

  const onToggleGridType = useCallback(() => {
    setGridType((prevType) => (prevType === GRID_TYPE_SETTINGS ? GRID_TYPE_HISTORY : GRID_TYPE_SETTINGS))
  }, [])

  const unsavedChangesButtons = useMemo(
    () => [
      {
        label: 'Save and Close',
        variant: 'outlined',
        color: 'primary',
        icon: <Iconify icon='material-symbols:save' width={20} color='#4caf50' />,
        onClick: async () => {
          setShowCloseWarning(false)
          const saved = await handleUpdate()
          if (saved) onClose()
        },
      },
      {
        label: 'Discard Changes',
        variant: 'outlined',
        color: 'error',
        icon: <Iconify icon='material-symbols:delete' width={20} color='#d32f2f' />,
        onClick: () => {
          setShowCloseWarning(false)
          onClose()
        },
      },
    ],
    [handleUpdate, onClose],
  )

  return (
    <Dialog
      fullWidth={true}
      maxWidth={'xlg'}
      PaperProps={{
        sx: {
          height: '80vh',
          width: '80vw',
          maxWidth: '95vw',
          backgroundColor: getChartBackColor(),
        },
      }}
      open={true}
      onClose={() => handleClose()}>
      <Box
        sx={{
          height: '44px',
          display: 'flex',
          alignItems: 'center',
          px: 1.5,
          color: appColors.headerTextColor,
          backgroundColor: getWindowBarColor(),
          borderBottom: '1px solid rgba(255,255,255,0.12)',
        }}>
        <Typography sx={{ fontWeight: 600, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {gridType === GRID_TYPE_SETTINGS ? `Data Bridge Settings` : ''}
        </Typography>

        <IconButton onClick={handleClose} size='small' sx={{ ml: 1 }}>
          <CloseIcon fontSize='small' />
        </IconButton>
      </Box>

      <Box
        sx={{
          height: 'calc(90vh - 44px)',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}>
        <Box sx={{ p: 1 }}>
          <DataBridgeRibbon
            gridType={gridType}
            onToggleGridType={onToggleGridType}
            onAdd={onAdd}
            onDelete={onDelete}
            onEdit={onEdit}
            selectedRow={selectedRow}
            changesMade={changesMade}
            onSave={handleUpdate}
            onDiscard={handleClose}
          />{' '}
        </Box>
        <Box
          sx={{
            backgroundColor: getBackColor(),
            justifyContent: 'center',
            alignItems: 'center',
            display: 'flex',
            width: '100%',
            p: 1,
          }}>
          <SearchBarWithRecent
            value={searchText}
            onChange={(newSearchTerm) => setSearchText(newSearchTerm)}
            onCancelSearch={() => setSearchText('')}
            style={searchBarStyle()}
            recentKey='emailSettings'
          />
        </Box>
        <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
          <Box
            className={getAgGridTheme()}
            sx={{
              height: '100%',
              width: '100%',
            }}>
            {gridType === GRID_TYPE_SETTINGS && (
              <AgGridReact
                loading={isLoading || isUpdating || isLoadingDropDowns}
                columnDefs={sortColDefs(columnDefsSettings, 'emailSettings')}
                defaultColDef={defaultColDefSettings}
                headerHeight={30}
                onFirstDataRendered={onFirstDataRenderedSettings}
                onGridReady={onGridReadySettings}
                onSelectionChanged={onSelectionChangedSettings}
                getContextMenuItems={getContextMenuItemsSettings}
                gridOptions={gridOptionsSettings}
                getRowId={getRowIdSettings}
                rowSelection='single'
              />
            )}
            {gridType === GRID_TYPE_HISTORY && (
              <AgGridReact
                loading={isLoadingHistory}
                columnDefs={sortColDefs(columnDefsHistory, 'dataBridgeHistory')}
                defaultColDef={defaultColDefHistory}
                headerHeight={30}
                onFirstDataRendered={onFirstDataRenderedHistory}
                onGridReady={onGridReadyHistory}
                getContextMenuItems={getContextMenuItemsHistory}
                gridOptions={gridOptionsHistory}
                onFilterChanged={onFilterChangedHistory}
                getRowId={getRowIdHistory}
              />
            )}
          </Box>
        </Box>
      </Box>
      {confirm.show && (
        <ConfirmDialog
          title={confirm.title}
          open={confirm.show}
          setOpen={() =>
            setConfirm({
              show: false,
              title: '',
              text: '',
              data: null,
            })
          }
          onConfirm={handleDelete}>
          {confirm.text}
        </ConfirmDialog>
      )}
      {dataBridgeUserModal.show && (
        <DataBridgeUserModal
          open={dataBridgeUserModal.show}
          action={dataBridgeUserModal.action}
          user={dataBridgeUserModal.user}
          dropDowns={dropDowns}
          onSave={onSaveDataBridgeUser}
          onClose={onCloseDataBridgeUserModal}
        />
      )}

      {showCloseWarning && (
        <ConfirmModal
          open={showCloseWarning}
          onClose={() => setShowCloseWarning(false)}
          title='Unsaved Changes'
          text='You have unsaved changes. Would you like to save before closing, or discard them?'
          buttons={unsavedChangesButtons}
        />
      )}

      <StatusSnackbar status={status} setStatus={setStatus} />
    </Dialog>
  )
}

export default DataBridgeModal
